import React, { useState, useEffect, useContext } from 'react';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import Loading from 'tbk-components/src/components/Loading';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Tabs from 'tbk-components/src/components/Tabs';
import Tab from 'tbk-components/src/components/Tab';
import LogoBar, { ILogoProps } from '../LogoBar';

/**
 * @useTermData
 * @taxonomy logo_group
 * @forceLinkedData
 */
export interface ITabbedLogoBarTerm {
	/**
	 * The title of the tab. Use "Link to Term Data" to connect this to the Logo categories.
	 */
	name?: string;
	/**
	 * This is only used to render the key internally, it doesn't need any UI.
	 * @noUI
	 */
	term_id?: number;
	/**
	 * The logos will be uploaded to the term in the admin with a custom field and injected at render time.
	 * @noUI
	 */
	logos?: ILogoProps[];
	/**
	 * This is set on the term in the admin with a custom field and injected at render time.
	 * @noUI
	 */
	cta?: ILink;
}

export interface ITabbedLogoBarProps extends IBaseElementProps {
	heading?: string;
	subHeading?: string;
	/**
	 * @control richtext
	 */
	description?: string;
	/**
	 * Select the Logo Groups to display
	 */
	tabbedLogoBarTabs?: ITabbedLogoBarTerm[];
}

/**
 * Calian Tabbed Logo Bar
 * @icon schedule
 * @block
 */
const TabbedLogoBar: React.FC<ITabbedLogoBarProps> = withAppContext(
	({
		id,
		className,
		classNames = [],
		heading,
		subHeading,
		description,
		tabbedLogoBarTabs,
		context,
	}) => {
		const { Api } = useContext(context);
		const [tabbedLogoBarTabData, setTabbedLogoBarTabData] = useState<
			ITabbedLogoBarTerm[]
		>([]);
		const [loading, setLoading] = useState<boolean>(false);

		// Update when data changes in editor.
		useEffect(() => {
			if (!tabbedLogoBarTabs) {
				return;
			}
			// If we're in the editor, the logos prop won't be populated.
			// Let's fetch them from the API.
			if (!tabbedLogoBarTabs[0]?.logos) {
				setLoading(true);
				Api.getTabbedLogoBarData({
					tabbedLogoBarTabs: tabbedLogoBarTabs,
				})
					.then(({ tabbedLogoBarTabs }) => {
						setTabbedLogoBarTabData(tabbedLogoBarTabs);
					})
					.finally(() => setLoading(false));
			} else {
				// On the front-end, props_for_hydration will populate the logos.
				setTabbedLogoBarTabData(tabbedLogoBarTabs);
			}
		}, [JSON.stringify(tabbedLogoBarTabs)]);

		return loading ? (
			<Loading />
		) : (
			<div
				id={id}
				className={
					className ||
					cn(
						'calian-tabbed-logo-bar container-fluid mw-1440px',
						...classNames,
					)
				}
			>
				<div className="calian-tabbed-logo-bar__tabs-container py-6 py-lg-11">
					{(heading || subHeading || description) && (
						<div className="calian-tabbed-logo-bar__upper mx-auto mb-5 mb-xl-9 text-center">
							{heading && (
								<h2 className="mx-auto mb-4">{heading}</h2>
							)}
							{subHeading && (
								<p className="lead mb-6">{subHeading}</p>
							)}
							{description && description !== '<br>' && (
								<div
									dangerouslySetInnerHTML={{
										__html: description,
									}}
								/>
							)}
						</div>
					)}
					{!!tabbedLogoBarTabData?.length && (
						<Tabs>
							{tabbedLogoBarTabData &&
								tabbedLogoBarTabData.length > 0 &&
								tabbedLogoBarTabData.map(
									(tabbedLogoBarTab, index) => (
										<Tab
											title={tabbedLogoBarTab.name}
											key={index}
										>
											<div className="calian-tabbed-logo-bar__tab-content-container container-fluid">
												<div className="row">
													<div className="col-12 text-center">
														{tabbedLogoBarTab.logos &&
															tabbedLogoBarTab
																.logos.length >
																0 && (
																<LogoBar
																	logos={
																		tabbedLogoBarTab.logos
																	}
																	layout="Simple"
																/>
															)}
														{tabbedLogoBarTab.cta &&
															tabbedLogoBarTab.cta
																.label && (
																<Button
																	className="calian-tabbed-logo-bar__tab-cta-button btn btn-primary mt-6"
																	{...tabbedLogoBarTab.cta}
																/>
															)}
													</div>
												</div>
											</div>
										</Tab>
									),
								)}
						</Tabs>
					)}
				</div>
			</div>
		);
	},
);

export default TabbedLogoBar;
